import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { User } from "../../../../../../../models/user";
import RoutePlannerDriverService from "../../../../../../../services/route.planner/driver.service";
import { RoutePlannerKeys, UserKeys } from "../../../../../../../util/queries";

const useRoutePlannerActions = () => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const useAddToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.createDriver(),
      mutationFn: (user: User) => RoutePlannerDriverService.create(user),
      onMutate: () => {
        NotificationToast.info(translate("addingToRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("addedToRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [UserKeys.all] });
        queryClient.refetchQueries({ queryKey: [UserKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotAddToRoutePlanner"));
      },
    });
  };

  const useUpdateToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.updateDriver(),
      mutationFn: ({ id, user }: { id: string; user: User }) =>
        RoutePlannerDriverService.update(id, user),
      onMutate: () => {
        NotificationToast.info(translate("updatingInRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("updatedInRoutePlanner"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotUpdateInRoutePlanner"));
      },
    });
  };

  const useRemoveFromRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.deleteFleet(),
      mutationFn: (id: number) => RoutePlannerDriverService.delete(id),
      onMutate: () => {
        NotificationToast.info(translate("removingFromRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("removedFromRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [UserKeys.all] });
        queryClient.refetchQueries({ queryKey: [UserKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotRemoveFromRoutePlanner"));
      },
    });
  };

  return {
    useAddToRoutePlanner,
    useUpdateToRoutePlanner,
    useRemoveFromRoutePlanner,
  };
};

export default useRoutePlannerActions;
